import * as Sentry from "@sentry/react";

// Type definitions for driver onroad test assessment
export interface DriverOnroadTestAssessment {
  id?: string;
  application_id: string;
  type: "driver-onroad-test";
  started_at?: string;
  completed_at?: string;
  question_num?: number;
  result_breakdown?: Record<string, unknown>;
  result?: "pass" | "fail";
  score?: number;
}

// Type for assessment update data
export interface AssessmentUpdateData {
  id: string;
  question_num?: number;
}

/**
 * Create a new driver onroad test assessment via cloud function
 */
export const createDriverOnroadTestAssessment = async (
  assessmentData: Partial<DriverOnroadTestAssessment>
): Promise<{
  success: boolean;
  message: string;
  assessment?: DriverOnroadTestAssessment;
}> => {
  try {
    // Get the function URL from environment variables
    const functionUrl = `${
      import.meta.env.VITE_SUPABASE_URL
    }/functions/v1/create-assessment`;

    // Get the anon key for authorization
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!functionUrl || !anonKey) {
      throw new Error("Missing Supabase configuration");
    }

    // Call the cloud function
    const response = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify(assessmentData),
    });

    // Parse the response
    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || "Failed to create driver onroad test assessment"
      );
    }

    return {
      success: true,
      message: "Driver onroad test assessment created successfully",
      assessment: data.assessment,
    };
  } catch (error) {
    console.error("Error in createDriverOnroadTestAssessment:", error);

    Sentry.captureException(error, {
      extra: {
        context: "driverOnroadTestService.createDriverOnroadTestAssessment",
        assessmentData: JSON.stringify(assessmentData),
      },
    });

    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : "Failed to create driver onroad test assessment",
    };
  }
};

/**
 * Update an existing driver onroad test assessment via cloud function
 */
export const updateDriverOnroadTestAssessment = async (
  updateData: Partial<AssessmentUpdateData>
): Promise<{
  success: boolean;
  message: string;
  assessment?: DriverOnroadTestAssessment;
}> => {
  try {
    // Get the function URL from environment variables
    const functionUrl = `${
      import.meta.env.VITE_SUPABASE_URL
    }/functions/v1/update-assessment`;

    // Get the anon key for authorization
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!functionUrl || !anonKey) {
      throw new Error("Missing Supabase configuration");
    }

    // Call the cloud function
    const response = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify(updateData),
    });

    // Parse the response
    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || "Failed to update driver onroad test assessment"
      );
    }

    return {
      success: true,
      message: "Driver onroad test assessment updated successfully",
      assessment: data.assessment,
    };
  } catch (error) {
    console.error("Error in updateDriverOnroadTestAssessment:", error);

    Sentry.captureException(error, {
      extra: {
        context: "driverOnroadTestService.updateDriverOnroadTestAssessment",
        assessmentId: updateData.id,
      },
    });

    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : "Failed to update driver onroad test assessment",
    };
  }
};
